import React, { useState, useEffect } from 'react';
import { Settings, Save, Copy, Check } from 'lucide-react';
import { fetchWidgetSettings, saveWidgetSettings, WidgetSettings } from '../../../services/whatsapp/widgetService';
import { settingsService } from '../../../services/whatsapp/settingsService';

export default function WidgetTab() {
  const [globalSettings, setGlobalSettings] = useState<any>({});
  
  useEffect(() => {
    return settingsService.subscribe(setGlobalSettings);
  }, []);
  const [settings, setSettings] = useState<WidgetSettings>({
    greeting: 'Chat with us on WhatsApp',
    position: 'right',
    color: '#25D366',
    text: 'Hi! I have a question. [ref:web]'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fallback to empty string if missing
  const phone = globalSettings?.businessNumbers?.[0]?.phone || '1234567890';

  useEffect(() => {
    fetchWidgetSettings()
      .then((data) => {
        setSettings(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await saveWidgetSettings(settings);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const widgetSnippet = `<script 
  src="https://amaplayer.com/wa-widget.js" 
  data-phone="${phone}" 
  data-text="${settings.text}" 
  data-color="${settings.color}" 
  data-position="${settings.position}" 
  data-greeting="${settings.greeting}"></script>`;

  const copySnippet = () => {
    navigator.clipboard.writeText(widgetSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return <div className="p-4 text-gray-400">Loading widget settings...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-white mb-2 flex items-center gap-2">
          <Settings className="w-5 h-5 text-emerald-500" />
          Web Chat Widget
        </h3>
        <p className="text-sm text-gray-400">
          Embed a WhatsApp chat button on your website. When users click it, they will be taken to WhatsApp with a pre-filled message.
        </p>
      </div>

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-800 rounded-lg text-sm text-red-400">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 max-w-2xl">
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Greeting Tooltip
          </label>
          <input
            type="text"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500 transition-colors"
            value={settings.greeting}
            onChange={(e) => setSettings({ ...settings, greeting: e.target.value })}
            placeholder="e.g. Chat with us"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1">
            Pre-filled Message
          </label>
          <input
            type="text"
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500 transition-colors"
            value={settings.text}
            onChange={(e) => setSettings({ ...settings, text: e.target.value })}
            placeholder="e.g. Hi! I need help with..."
          />
          <p className="text-xs text-gray-500 mt-1">
            Include a tag like [ref:web] to attribute conversations to your website.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Position
            </label>
            <select
              className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500 transition-colors appearance-none"
              value={settings.position}
              onChange={(e) => setSettings({ ...settings, position: e.target.value as 'left' | 'right' })}
            >
              <option value="right">Bottom Right</option>
              <option value="left">Bottom Left</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1">
              Button Color
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                className="h-10 w-10 bg-slate-800 border border-slate-700 rounded-lg cursor-pointer p-1"
                value={settings.color}
                onChange={(e) => setSettings({ ...settings, color: e.target.value })}
              />
              <input
                type="text"
                className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500 transition-colors"
                value={settings.color}
                onChange={(e) => setSettings({ ...settings, color: e.target.value })}
              />
            </div>
          </div>
        </div>

        <div className="pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-colors flex items-center gap-2 font-medium disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>

      <div className="pt-8 border-t border-slate-800">
        <h4 className="text-md font-medium text-white mb-4">Installation Snippet</h4>
        <p className="text-sm text-gray-400 mb-3">
          Copy this code and paste it just before the closing <code className="text-emerald-400">{'</body>'}</code> tag on your website.
        </p>
        <div className="relative group">
          <pre className="bg-slate-900 border border-slate-700 rounded-lg p-4 text-sm text-gray-300 overflow-x-auto whitespace-pre-wrap">
            {widgetSnippet}
          </pre>
          <button
            onClick={copySnippet}
            className="absolute top-3 right-3 p-2 bg-slate-800 hover:bg-slate-700 text-gray-300 rounded-md transition-colors shadow-sm"
            title="Copy to clipboard"
          >
            {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
          </button>
        </div>
      </div>
    </div>
  );
}
